import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Button, Typography } from '@mui/material';
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import useForceUpdateTranslation from '@utils/useForceUpdateTranslation';

import { setSidebarCollapsed, setSidebarToggled, setNaviData } from '@modules/ui';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import {
  PanelRightClose,
  PanelRightOpen,
  LayoutDashboard,
  SquarePlay,
  FileText,
  BookText,
  Cloud,
  ChartSpline,
  BrainCircuit,
  Radio,
  Laptop
} from 'lucide-react';

const LeftSidebar = () => {
  const sidebarCollapsed = useSelector(state => state.ui.sidebarCollapsed);
  const sidebarToggled = useSelector(state => state.ui.sidebarToggled);
  const uuid = useSelector(state => state.ui.uuid);
  const userData = useSelector(state => state.organization.userData);
  const organizationData = useSelector(state => state.organization.organizationData);
  const theme = useSelector(state => state.ui.theme);

  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useForceUpdateTranslation();

  const isActive = (path) => {
    const currentPath = location.pathname;
    if (path === '/') {
      return currentPath === '/' ? 'ps-active' : '';
    }
    if (path.includes('/vod')) {
      const specialPaths = ['/vod/bulkDeleter', '/vod/originalVodUploader'];
      if (specialPaths.includes(path)) {
        return currentPath === path ? 'ps-active' : '';
      }
      if (path === '/vod') {
        return currentPath.startsWith('/vod') && !specialPaths.includes(currentPath) ? 'ps-active' : '';
      }
    }
    return currentPath.startsWith(path) ? 'ps-active' : '';
  };

  const [navOpen, setNavOpen] = useState(false);
  const [tmpNavData, setTmpNavData] = useState([]);

  useEffect(() => {
    if (Object.keys(userData).length > 0 && userData.naviData) {
      try {
        const parsedNaviData = JSON.parse(userData.naviData);
        const formattedNaviData = [];

        for (const data of parsedNaviData) {
          if (data.route === '/') {
            data.id = 'dashboard';
            formattedNaviData.push(data);
          } else {
            const routeParts = data.route.split('/');
            data.id = routeParts[1];
            if (routeParts[2] === 'parent') {
              const childArr = [];
              for (const child of data.children) {
                const tmpChild = { ...child };
                tmpChild.id = child.route.split('/').length === 2 ? 'manage' : child.route.split('/')[2];
                childArr.push(tmpChild);
              }
              formattedNaviData.push({ ...data, children: childArr });
            } else {
              formattedNaviData.push(data);
            }
          }
        }
        setTmpNavData(formattedNaviData);
        dispatch(setNaviData(formattedNaviData));
      } catch (error) {
        console.error('Error parsing naviData:', error);
      }
    }
  }, [userData]);

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'Home':
        return HomeIcon;
      case 'LayoutDashboard':
        return LayoutDashboard;
      case 'SquarePlay':
        return SquarePlay;
      case 'FileText':
        return FileText;
      case 'BookText':
        return BookText;
      case 'Cloud':
        return Cloud;
      case 'ChartSpline':
        return ChartSpline;
      case 'BrainCircuit':
        return BrainCircuit;
      case 'Radio':
        return Radio;
      case 'Laptop':
        return Laptop;
      default:
        return null;
    }
  };

  return (
    <Sidebar
      id="wrapper-left-sidebar"
      collapsed={sidebarCollapsed}
      toggled={sidebarToggled}
      breakPoint="md"
      collapsedWidth="80px"
      style={{
        borderRight: `0px`
      }}
      onBackdropClick={() => dispatch(setSidebarToggled(!sidebarToggled))}
    >
      <div
        id="left-sidebar-head"
        className="left-sidebar-head"
      >
        <Button
          size="large"
          style={{
            border: 0,
            cursor: 'pointer',
            textTransform: 'none',
            fontSize: '1.25rem',
            width: '100%',
            height: '100%',
            paddingLeft: '30px',
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center'
          }}
          component="label"
          variant="outline-secondary"
          tabIndex={-1}
          startIcon={
            !sidebarCollapsed ? (
              <PanelRightOpen className="btn-ctr-panel" style={{ fontSize: '1.5rem' }} />

            ) : (
              <PanelRightClose className="btn-ctr-panel" style={{ fontSize: '1.5rem' }} />

            )
          }
          onClick={() => dispatch(setSidebarCollapsed(!sidebarCollapsed))}
        >
          {
            !sidebarCollapsed && (
              theme !== 'auto' && theme !== 'dark' && theme !== 'light' ? (
                // auto, dark, light가 아닐 경우 다른 엘리먼트
                <div className="logo-organization"
                  style={{
                    fontWeight: 'bold',
                    backgroundImage: `url('https://cdn.coursemos.io/logo/${organizationData.organization_code}_logo.png')`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    width: '80%',
                    height: '55px',
                    display: 'inline-block'
                  }}>

                </div>
              ) : (
                // auto, dark, light일 경우 기본 텍스트
                <div style={{ fontWeight: 'bold' }}>
                  Studio
                </div>
              )
            )
          }
        </Button>
      </div>

      <Menu
        id="left-sidebar-menu"
        renderExpandIcon={({ open }) =>
          open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
        }
        className={navOpen ? 'active' : ''}
      >
        {tmpNavData.length > 0 && Object.keys(userData).length > 0 &&
          tmpNavData.map((item, index) => {
            console.log('item.icon', item.icon);
            const IconComponent = getIconComponent(item.icon);
            console.log('IconComponent', IconComponent);

            if (item.children && item.children.length > 0) {
              return (
                <SubMenu
                  key={index}
                  label={t(`navi_data.${item.id}.label`)}
                  className={isActive(item.route.includes('parent') ? item.route.replace('/parent', '') : item.route)}
                  icon={IconComponent ? <IconComponent /> : null}
                >
                  {item.children.map((child, childIndex) => (
                    <MenuItem
                      key={childIndex}
                      component={<Link to={child.route} />}
                      className={isActive(child.route)}
                    >
                      <Typography variant="sidebar">{t(`navi_data.${item.id}.${child.id}.label`)}</Typography>
                    </MenuItem>
                  ))}
                </SubMenu>
              );
            } else {
              return (
                <MenuItem
                  key={index}
                  icon={IconComponent ? <IconComponent /> : null}
                  component={<Link to={item.route} />}
                  className={location.pathname === item.route ? 'ps-active' : ''}
                >
                  <Typography variant="sidebar">{t(`navi_data.${item.id}.label`)}</Typography>
                </MenuItem>
              );
            }
          })}
      </Menu>
    </Sidebar >
  );
};

export default LeftSidebar;